import { useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { userHasAccessToken } from '@utils/authentication'

export { RouteGuard, isPublicRoute }

const PUBLIC_PATHS = [
  '/',
  '/locations',
  '/terms',
  '/about',
  '/auth',
  '/contact-us',
  '/ktn-lookup',
  '/important-notices',
  '/status',
  '/renewal/eligibility/eligibility',
  '/cancel',
  '/modify',
  '/enroll/you',
  '/enroll/finalize/confirmation',
  '/404',
]

function getPublicPaths(hideEnrollmentFlow: boolean) {
  return hideEnrollmentFlow
    ? PUBLIC_PATHS.filter((path) => path !== '/locations' && path !== '/about')
    : PUBLIC_PATHS
}

// Pathname, not asPath: the query string differs between export and hydration.
function isPublicRoute(pathname: string, hideEnrollmentFlow: boolean): boolean {
  return (
    getPublicPaths(hideEnrollmentFlow).includes(pathname) ||
    pathname.startsWith('/help')
  )
}

function RouteGuard({ children }) {
  const router = useRouter()
  const hideEnrollmentFlow =
    process.env.NEXT_PUBLIC_HIDE_ENROLLMENT_FLOW === 'true'

  // Effects don't run at export, so public routes must render on the first pass.
  const [authorized, setAuthorized] = useState(() =>
    isPublicRoute(router.pathname, hideEnrollmentFlow)
  )

  useEffect(() => {
    // on initial load - run auth check
    AuthCheck(router.asPath)
    // on initial load - run hide enrollment flow check
    EnrollmentFlowRedirect(router.asPath)

    // on route change start - hide page content by setting authorized to false
    const hideContent = () => setAuthorized(false)
    router.events.on('routeChangeStart', hideContent)
    router.events.on('hashChangeStart', hideContent)

    // on route change complete - run auth check
    router.events.on('routeChangeComplete', AuthCheck)
    router.events.on('hashChangeComplete', AuthCheck)
    router.events.on('routeChangeComplete', EnrollmentFlowRedirect)
    router.events.on('hashChangeComplete', EnrollmentFlowRedirect)

    // unsubscribe from events in useEffect return function
    return () => {
      router.events.off('routeChangeStart', hideContent)
      router.events.off('routeChangeComplete', AuthCheck)
      router.events.off('hashChangeStart', hideContent)
      router.events.off('hashChangeComplete', AuthCheck)
      router.events.off('routeChangeComplete', EnrollmentFlowRedirect)
      router.events.off('hashChangeComplete', EnrollmentFlowRedirect)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function AuthCheck(url: string) {
    // redirect to login page if accessing a private page and not logged in
    const publicPaths = getPublicPaths(hideEnrollmentFlow)

    const hasTestQueryString = url.split('?')[1]?.includes('test=true') ?? false
    const urlParts = url.split('?')
    const path = urlParts[0]
    const queryString = urlParts.slice(1).join('&')
    const queryParams = new URLSearchParams(queryString)
    const desiredPath = queryParams.get('desiredPath') || path
    const enrollmentId = queryParams.get('enrollmentId') || null
    const territoryId = queryParams.get('territoryId') || null

    if (
      !userHasAccessToken() &&
      !publicPaths.includes(desiredPath) &&
      !desiredPath.startsWith('/help') &&
      !hasTestQueryString
    ) {
      setAuthorized(false)
      router.push(
        {
          pathname: '/auth',
          query: {
            desiredPath,
            enrollmentId,
            territoryId,
          },
        },
        '/auth'
      )
    } else {
      setAuthorized(true)
    }
  }

  function EnrollmentFlowRedirect(url) {
    if (hideEnrollmentFlow) {
      if (url.includes('enroll')) {
        router.push({
          pathname: '/',
        })
      }
    }
  }

  return authorized && children
}
